import { useTranslation } from 'react-i18next';
import { useSupervisor } from '@/hooks/useSupervisor';
import type { Order } from '@/types/supervisor';
import SetPrintSizeWindow from './SetPrintSizeWindow';
import { normalizePrintSizeValue, type PrintSizeValue } from './utils/printSizeForm';
import ItemStepPanelLayout from './ItemStepPanelLayout';
import { usePrintableItemTargets, useSelectionGuard } from './hooks/usePrintableItemTargets';
import type { OrderItemPricingRequest } from '@/services/pricingService';

interface SizeStepPanelProps {
  readonly order: Order;
  readonly onUpdatePrintSize?: (itemId: string, size: PrintSizeValue) => void;
}

const SizeStepPanel = ({ order, onUpdatePrintSize }: SizeStepPanelProps) => {
  const { t } = useTranslation();
  const { send: sendToSupervisor } = useSupervisor();

  const { printableItems, targetedItems, hasSelectedItems } = usePrintableItemTargets(order);

  useSelectionGuard({
    printableItems,
    hasSelectedItems,
    send: sendToSupervisor,
    aiPrompt: 'Politely explain that the user needs to select at least one uploaded image before adjusting print size.',
    fallbackMessage: t(
      'uploadedImages.aiFallback.size.noSelection',
      'Select at least one image to adjust print size.',
    ),
  });

  const handleExit = () => {
    sendToSupervisor({ type: 'UI.GOTO_STEP', step: 'uploadedImages' });
  };

  return (
    <ItemStepPanelLayout<PrintSizeValue>
      t={t}
      availableItems={printableItems}
      items={targetedItems}
      title={t('canvas.steps.selectItemForSizing', 'Choose item to size')}
      emptyStateMessage={t('setPrintSize.noItem', 'Select an image to adjust its print size.')}
      uploadCtaLabel={t('orderSummary.goToUpload', 'Upload images')}
      onUploadClick={() => sendToSupervisor({ type: 'UI.GOTO_STEP', step: 'upload' })}
      onExit={handleExit}
      enableUnsavedExitGuard
      pristineSaveLabel={t('setPrintSize.confirmCta', 'Confirm size')}
      dirtySaveLabel={t('setPrintSize.saveCta', 'Save size')}
      discardChangesTitle={t('setPrintSize.discardChangesTitle', 'Discard unsaved size changes?')}
      discardChangesDescription={t('setPrintSize.discardChangesDescription', 'Your size changes are not saved yet. You can keep editing or discard them and leave this screen.')}
      discardChangesConfirmText={t('setPrintSize.discardChangesConfirm', 'Discard changes')}
      discardChangesCancelText={t('setPrintSize.discardChangesCancel', 'Keep editing')}
      resolveItemValue={(item) => {
        return normalizePrintSizeValue({
          widthIn: item.widthIn ?? undefined,
          heightIn: item.heightIn ?? undefined,
          marginIn: item.marginIn,
          galleryWrapIn: item.galleryWrapIn,
          galleryWrapMirrorEdges: item.galleryWrapMirrorEdges,
        }) ?? undefined;
      }}
      areValuesEqual={(left, right) => {
        const normalizedLeft = normalizePrintSizeValue(left);
        const normalizedRight = normalizePrintSizeValue(right);

        return normalizedLeft?.widthIn === normalizedRight?.widthIn
          && normalizedLeft?.heightIn === normalizedRight?.heightIn
          && normalizedLeft?.marginIn === normalizedRight?.marginIn
          && normalizedLeft?.galleryWrapIn === normalizedRight?.galleryWrapIn
          && normalizedLeft?.galleryWrapMirrorEdges === normalizedRight?.galleryWrapMirrorEdges;
      }}
      onSave={(itemId, size) => {
        if (onUpdatePrintSize) {
          onUpdatePrintSize(itemId, size);
        }
      }}
      resolvePricingPreviewRequest={(item, value): OrderItemPricingRequest | null => {
        if (!item.uploadedFileId) {
          return null;
        }
        if (
          typeof value?.widthIn !== 'number'
          || value.widthIn <= 0
          || typeof value?.heightIn !== 'number'
          || value.heightIn <= 0
        ) {
          return null;
        }

        return {
          uploadedFileId: item.uploadedFileId,
          widthInches: value.widthIn,
          heightInches: value.heightIn,
          galleryWrapInches: typeof value.galleryWrapIn === 'number' && value.galleryWrapIn > 0 ? value.galleryWrapIn : undefined,
          paperTypeId: item.paperTypeId ?? undefined,
          printerId: item.printerId ?? undefined,
        };
      }}
      renderWindow={({ item, onClose, onBack, onSave, draftValue, setDraftValue, setDraftValidity, clearDraftValue, saveDisabled, saveLabel }) => (
        <SetPrintSizeWindow
          key={item.id}
          item={item}
          onClose={onClose}
          onBack={onBack}
          onSave={onSave}
          saveDisabled={saveDisabled}
          saveLabel={saveLabel}
          draftSize={draftValue}
          onDraftChange={setDraftValue}
          onDraftValidityChange={setDraftValidity}
          onDraftReset={clearDraftValue}
          onRequestPrintQualityHelp={(warning) => {
            sendToSupervisor({
              type: 'UI.REQUEST_AI_PROMPT',
              aiPrompt: `Explain briefly and politely why print files should be at least ${warning.threshold} PPI for printing, and tell the user this image is currently about ${Math.round(warning.ppi)} PPI at the selected size. Ask whether they want to keep this quality or adjust the print size.`,
              fallbackMessage: t(
                'setPrintSize.lowPpiAiFallback',
                'This image is {{ppi}} PPI at the selected print size. For sharp printed results, {{threshold}} PPI or higher is recommended.',
                { ppi: Math.round(warning.ppi), threshold: warning.threshold },
              ),
            });
          }}
        />
      )}
    />
  );
};

export default SizeStepPanel;
